import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import usePackingOrderService from '../../../app/services/hooks/usePackingOrderService';
import useProductService from '../../../app/services/hooks/useProductService';
import useCenterService from '../../../app/services/hooks/useCenterService';
import useWarehouseService from '../../../app/services/hooks/useWarehouseService';
import { PackingOrderDataForm, defaultPackingOrderDataForm } from '../../../app/models/PackingOrder';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import ButtonCreate from '../../../components/buttons/ButtonCreate';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import SelectTwo from '../../../components/SelectTwo';
import PackingOrderForm from './forms/PackingOrderForm';

interface Props {
  onSaved?: (packingOrderId?: number) => void;
  onCancel?: () => void;
  onError?: () => void;
}

type SelectOpt = { id: number | string; name: string };
type ProductOption = { id: number; name: string; unit?: string | null };

export interface OutputProductLine {
  tempId: number;
  product_id?: number;
  quantity: number;
}

const PackingOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { createPackingOrder, storePackingOrder, fetchingStorePackingOrder } = usePackingOrderService();
  const { getProducts } = useProductService();
  const { getCenters } = useCenterService();
  const { getWarehouses } = useWarehouseService();

  const [packingOrder, setPackingOrder] = useState<PackingOrderDataForm>({
    ...defaultPackingOrderDataForm,
    status: 'CREATED'
  });
  const [societies, setSocieties] = useState<SelectOpt[]>([]);
  const [centers, setCenters] = useState<SelectOpt[]>([]);
  const [warehouses, setWarehouses] = useState<SelectOpt[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [outputProductLines, setOutputProductLines] = useState<OutputProductLine[]>([]);
  const [nextTempId, setNextTempId] = useState(1);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => {
    create();
    loadProducts();
  }, []);

  const create = () => {
    showLoading?.('loading', 'Cargando formulario...');
    createPackingOrder({
      onSuccess: (response: ServiceResponse) => {
        const list = response.data.societies || [];
        setSocieties(list);

        // Preseleccionar la primera sociedad activa (o la primera)
        if (list.length > 0) {
          const firstActive = list.find((s: any) => s.is_active ?? s.is_active === true);
          const defaultSocId = Number(firstActive?.id ?? list[0].id);
          setPackingOrder((prev) => ({
            ...prev,
            society_id: defaultSocId,
            center_id: undefined,
            warehouse_id: undefined,
          }));
          onSocietyChangeForCenters(defaultSocId);
        }
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
        onError?.();
      }
    });
  };

  const loadProducts = () => {
    getProducts(
      { page: 1, per_page: 500, sort: 'id', order: 'asc' },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.products || [];
          setProducts(
            list.map((p: any) => ({
              id: p.id,
              name: p.name || p.sale_name || '',
              unit: p.unit ?? null,
            }))
          );
        }
      }
    );
  };

  const onSocietyChangeForCenters = (societyId?: number) => {
    if (!societyId) {
      setCenters([]);
      setWarehouses([]);
      return;
    }
    getCenters(
      { page: 1, per_page: 500, sort: 'name', order: 'asc', society_id: societyId },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.centers || [];
          const mapped = list.map((c: any) => ({ id: c.id, name: c.name || '' }));
          setCenters(mapped);
          setWarehouses([]);

          if (mapped.length > 0) {
            const firstActive = list.find((c: any) => c.is_active ?? c.is_active === true);
            const defaultCenterId = Number(firstActive?.id ?? mapped[0].id);
            setPackingOrder((prev) => ({
              ...prev,
              center_id: defaultCenterId,
              warehouse_id: undefined,
            }));
            onCenterChangeForWarehouses(defaultCenterId);
          }
        }
      }
    );
  };

  const onCenterChangeForWarehouses = (centerId?: number) => {
    if (!centerId) {
      setWarehouses([]);
      return;
    }
    getWarehouses(
      { page: 1, per_page: 500, sort: 'name', order: 'asc', center_id: centerId },
      {
        onSuccess: (response: ServiceResponse) => {
          const list = response.data?.warehouses || [];
          setWarehouses(list.map((w: any) => ({ id: w.id, name: w.name || '' })));
        }
      }
    );
  };

  const addOutputLine = () => {
    setOutputProductLines((prev) => [...prev, { tempId: nextTempId, quantity: 0 }]);
    setNextTempId((n) => n + 1);
  };

  const updateOutputLine = (tempId: number, patch: Partial<OutputProductLine>) => {
    setOutputProductLines((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row))
    );
  };

  const removeOutputLine = (tempId: number) => {
    setOutputProductLines((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const store = () => {
    if (!packingOrder.society_id) {
      toast.error('Debe seleccionar una sociedad');
      return;
    }
    if (!packingOrder.program_date) {
      toast.error('Debe indicar la fecha de programa');
      return;
    }
    if (!packingOrder.center_id) {
      toast.error('Debe seleccionar un centro');
      return;
    }
    if (!packingOrder.warehouse_id) {
      toast.error('Debe seleccionar un almacén');
      return;
    }
    if (packingOrder.is_periodic) {
      if (!packingOrder.recurrence_days?.length) {
        toast.error('Debe seleccionar al menos un día de la semana');
        return;
      }
      if (!packingOrder.end_date) {
        toast.error('Debe indicar la fecha hasta la cual crear órdenes');
        return;
      }
      if (packingOrder.end_date && packingOrder.program_date && packingOrder.end_date < packingOrder.program_date) {
        toast.error('La fecha "Hasta el" debe ser igual o posterior a la fecha de inicio');
        return;
      }
    }

    const output_products = outputProductLines
      .filter((row) => row.product_id && row.quantity > 0)
      .map((row) => ({
        product_id: row.product_id,
        quantity: row.quantity,
      }));

    const payload: Record<string, unknown> = {
      society_id: packingOrder.society_id,
      center_id: packingOrder.center_id ?? null,
      warehouse_id: packingOrder.warehouse_id ?? null,
      program_date: packingOrder.program_date || null,
      status: 'CREATED',
      output_products
    };
    if (packingOrder.is_periodic) {
      payload.is_periodic = true;
      payload.recurrence_days = packingOrder.recurrence_days ?? [];
      payload.end_date = packingOrder.end_date ?? null;
    }

    showLoading?.('loading', packingOrder.is_periodic ? 'Creando órdenes...' : 'Guardando orden de empaque...');
    storePackingOrder(payload, {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        const count = response.data?.created_count ?? 1;
        toast.success(
          response.message || (count > 1 ? `Se crearon ${count} órdenes.` : 'Orden creada.'),
          { autoClose: 2500 }
        );
        const newId: number | undefined = response.data?.packing_order_id ?? undefined;
        onSaved?.(newId);
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  const productOptions = products.map((p) => ({ value: p.id, label: String(p.name) }));

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PackingOrderForm
            packingOrder={packingOrder}
            setPackingOrder={setPackingOrder}
            societies={societies}
            centers={centers}
            warehouses={warehouses}
            errorFields={errorFields}
            hideStatus
            onlySocietyAndProgramDate
            onSocietyChange={onSocietyChangeForCenters}
            onCenterChange={onCenterChangeForWarehouses}
          />
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0 card-title">Productos resultado</h6>
                <ButtonCreate callbackCreate={addOutputLine} title="Agregar producto" />
              </div>
              <p className="text-muted small mb-2">
                Indique qué productos desea elaborar y la cantidad requerida. Puede agregar más de un producto.
              </p>
              <div className="table-responsive" style={{ minHeight: 220 }}>
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ width: 140 }}>Cantidad</th>
                      <th style={{ width: 180 }}>Unidad</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {outputProductLines.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">
                          Sin productos. Use &quot;Agregar producto&quot; para indicar los productos a elaborar.
                        </td>
                      </tr>
                    )}
                    {outputProductLines.map((row) => {
                      const product = products.find((p) => p.id === row.product_id);
                      const unitName = product?.unit ?? '';
                      return (
                        <tr key={row.tempId}>
                          <td>
                          <SelectTwo
                            name={`product_${row.tempId}`}
                            inputValue={row.product_id ?? null}
                            options={productOptions}
                            onChange={(v: any) => updateOutputLine(row.tempId, { product_id: v?.value })}
                            placeholder="Seleccione producto"
                            size="small"
                          />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              className="form-control form-control-sm"
                              value={row.quantity || ''}
                              onChange={(e) =>
                                updateOutputLine(row.tempId, { quantity: parseFloat(e.target.value) || 0 })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={unitName}
                              readOnly
                              placeholder="Unidad del producto"
                            />
                          </td>
                          <td>
                            <ButtonTableAction
                              callbackFunction={() => removeOutputLine(row.tempId)}
                              classIcon="mdi mdi-delete"
                              colorIcon="text-danger"
                              title="Quitar"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr />
      <div className="row justify-content-end">
        <div className="col-auto">
          <ButtonCancelForm callbackCancel={onCancel ?? (() => undefined)} locked={!!fetchingStorePackingOrder} />
        </div>
        <div className="col-auto">
          <ButtonSaveForm callbackSave={store} locked={!!fetchingStorePackingOrder} />
        </div>
      </div>
    </>
  );
};

export default PackingOrderCreate;
